import React, { useCallback, useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { inr, today, formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { askReason } from "../ApprovalLock";
import { field, label, BalanceBox } from "./shared";

const EMPTY = () => ({ amount: "", date: today(), reason: "" });

const NoteList = ({ notes, canWrite, onDelete }) => {
  if (notes.length === 0) return null;
  return (
    <div data-testid="cn-existing-list" className="space-y-2">
      <div className="text-sm font-medium">Credit notes issued ({notes.length})</div>
      {notes.map((c) => (
        <div key={c.id} data-testid={`cn-existing-${c.id}`} className="rounded-md border border-border p-2.5 text-sm flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="num font-semibold">{inr(c.amount)}<span className="num text-xs text-accent ml-2">{c.number}</span></div>
            <div className="num text-xs text-muted-foreground truncate">{c.date}{c.reason ? ` · ${c.reason}` : ""}</div>
          </div>
          {canWrite && (
            <button data-testid={`cn-del-${c.id}`} onClick={() => onDelete(c)} title="Delete credit note" className="h-8 w-8 rounded-md border border-input flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors shrink-0"><Trash2 size={13} /></button>
          )}
        </div>
      ))}
    </div>
  );
};

const NoteForm = ({ form, setForm, hasExisting }) => (
  <>
    {hasExisting && <div className="text-sm font-medium pt-1">Add another credit note</div>}
    <div className="grid grid-cols-2 gap-2">
      <div>
        <label className={label}>Amount (₹)</label>
        <input data-testid="cn-amount" type="number" step="0.01" min="0" className={field} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="e.g. 500" />
      </div>
      <div>
        <label className={label}>Date</label>
        <input data-testid="cn-date" type="date" className={field + " num"} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
      </div>
    </div>
    <div>
      <label className={label}>Reason</label>
      <input data-testid="cn-reason" className={field} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. goods returned, rate correction" />
    </div>
    <p className="text-xs text-muted-foreground">A credit note reduces what this party owes (it does not move cash). It reflects in the invoice balance, the party ledger and reports.</p>
  </>
);

export const CreditNoteDialog = ({ invoice, onClose, onChanged, user, canWrite, reloadLocks }) => {
  const [cn, setCn] = useState(null);
  const [notes, setNotes] = useState([]);
  const [form, setForm] = useState(EMPTY());

  const fetchNotes = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/invoices/${id}/credit-notes`);
      setNotes(data.notes || []);
      setCn((c) => (c ? { ...c, balance: data.balance, credit_note_total: data.credit_note_total } : c));
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    setCn(invoice); setNotes([]); setForm(EMPTY());
    if (invoice) fetchNotes(invoice.id);
  }, [invoice, fetchNotes]);

  const refresh = async () => { await fetchNotes(cn.id); onChanged(); };

  const add = async () => {
    const amt = parseFloat(form.amount);
    if (!(amt > 0)) return toast.error("Enter a valid amount");
    if (amt > cn.balance + 0.001) return toast.error(`Exceeds outstanding balance ${inr(cn.balance)}`);
    try {
      await api.post(`/invoices/${cn.id}/credit-notes`, { date: form.date, amount: amt, reason: form.reason });
      toast.success("Credit note added");
      setForm(EMPTY());
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete credit note ${c.number} (${inr(c.amount)})?`)) return;
    const reason = askReason(user, "deleting credit note", c.number);
    if (reason === null) return;
    try {
      const res = await api.delete(`/invoices/${cn.id}/credit-notes/${c.id}`, { params: { reason } });
      if (res.data?.status === "pending") { toast.info(res.data.message); reloadLocks(); onClose(); return; }
      toast.success("Credit note deleted"); await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const open = cn && cn.balance > 0.001;
  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card">
        <DialogHeader><DialogTitle className="font-display text-2xl">Credit Note · {cn?.invoice_no}</DialogTitle></DialogHeader>
        {cn && (
          <div className="space-y-3">
            <BalanceBox><span className="num font-bold text-destructive">{inr(cn.balance)}</span></BalanceBox>
            <NoteList notes={notes} canWrite={canWrite} onDelete={remove} />
            {open ? <NoteForm form={form} setForm={setForm} hasExisting={notes.length > 0} /> : (
              <div data-testid="cn-fully-settled" className="rounded-md bg-[hsl(var(--positive))]/10 text-[hsl(var(--positive))] text-sm font-medium p-3 text-center">Nothing outstanding on this invoice. You can delete a credit note above to restore the balance.</div>
            )}
          </div>
        )}
        <DialogFooter>
          {open && <button data-testid="cn-save" onClick={add} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Add credit note</button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
